import React, { useState } from 'react';
import BaseCard from '../base/BaseCard';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import { Theme, ThemeProps } from '@theme/base';
import { StarRating } from '@components/common/star-rating';
import { Icon } from '@components/common/generic-icon';
import { TestId } from '@components/test-ids';
import { Divider } from '@components/common/divider';
import { useI18NextContext } from '@components/hooks';
import { InfoCardModal } from '@components/common/modal';
import { Image } from '@model/common/image';
import { ReadMore } from '@styles/common';

export interface AccommodationCardProps {
  name: string;
  description: string | null;
  rating: number | null;
  ratingType?: string;
  resort: string;
  country: string;
  address?: string;
  telephone?: string | null;
  website?: string;
  gallery: Array<Image>;
  nights: number;
  theme?: Theme;
  className?: string;
  cabinInformation?: string | null;
}

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%'
});

const Title = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h4 as any),
  marginBottom: theme.custom.spacing.medium,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}));

const Details = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  paddingRight: theme.custom.spacing.large,
  marginTop: theme.custom.spacing.medium
}));

const DetailsIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: '2rem',
  color: theme.custom.colors.group4.light,
  paddingRight: theme.custom.spacing.xSmall
}));

const AttributesWrapper = styled.div({
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center'
});

const Attribute = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  paddingRight: theme.custom.spacing.xSmall * 2,
  color: theme.custom.colors.group4.light,
  ['&:last-of-type']: {
    paddingRight: 0
  }
}));

const SectionDivider = styled(Divider)(({ theme }: ThemeProps) => ({
  margin: `${theme.custom.spacing.large}px 0`
}));

const Destination = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  marginBottom: theme.custom.spacing.xSmall
}));

const MoreDetails = styled(ReadMore)({
  paddingBottom: 0,
  marginTop: 0,
  textDecoration: 'underline'
});

export const AccommodationCardComponent = ({
  name,
  description,
  rating,
  ratingType,
  nights,
  resort,
  country,
  gallery = [],
  className,
  cabinInformation
}: AccommodationCardProps) => {
  const t: any = useI18NextContext();
  const [showModal, setShowModal] = useState(false);
  const hero: string = (gallery.length && gallery[0].url) || '';
  return (
    <BaseCard
      className={className}
      image={hero}
      disableRipple
      renderBody={() => (
        <Container data-testid={TestId.card.accommodation.container}>
          <InfoCardModal
            testId={TestId.modal.main}
            show={showModal}
            onDismiss={() => setShowModal(!showModal)}
            title={name}
            gallery={gallery.map(({ url }: Image) => url)}
            description={description || ''}
            cabinInformation={cabinInformation}
          />

          <div>
            <Title>{name}</Title>
            <StarRating rating={rating} size={20} ratingType={ratingType} />
            {nights && (
              <Details>
                <DetailsIcon name={'mhi-nights'} />
                <AttributesWrapper>
                  <Attribute>
                    {nights} {t('common__label--nights')}
                  </Attribute>
                </AttributesWrapper>
              </Details>
            )}
          </div>
          <SectionDivider />
          <div>
            <Destination>
              {resort}, {country}
            </Destination>
            <MoreDetails data-testid={TestId.card.accommodation.moreDetails} onClick={() => setShowModal(true)}>
              {t('common__description--more-details')}
            </MoreDetails>
          </div>
        </Container>
      )}
    />
  );
};

export const AccommodationCard = withTheme(AccommodationCardComponent);
